import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiConfig } from '../../core/api-config';

export type EcbCourseKind = 'SHORT' | 'QUALIFICATION';
export interface EcbOccurrence {
  id: number;
  startDate: string;
  endDate: string;
  location: string;
  time: string;
  deliveryMode: string;
  seatsAvailable: number;
  price: number;
}
export interface EcbOccurrenceOptions {
  courseId: number;
  kind: EcbCourseKind;
  title: string;
  code: string;
  imageUrl: string;
  currency: string;
  occurrences: EcbOccurrence[];
}
export interface EcbCartLine {
  kind: EcbCourseKind;
  courseId: number;
  occurrenceId: number;
  title: string;
  code: string;
  imageUrl: string;
  currency: string;
  price: number;
  startDate: string;
  endDate: string;
  location: string;
  time: string;
  deliveryMode: string;
}
export interface EcbCheckoutRequest {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  companyName: string;
  items: { kind: EcbCourseKind; courseId: number; occurrenceId: number }[];
}

/** New occurrence-specific local cart. Only IDs are trusted by the server at checkout. */
@Injectable({ providedIn: 'root' })
export class UnifiedCartService {
  private readonly key = 'ecb_unified_cart_v1';
  readonly items$ = new BehaviorSubject<EcbCartLine[]>(this.read());
  constructor(private http: HttpClient) {}

  get items(): EcbCartLine[] { return this.items$.value; }
  get count(): number { return this.items.length; }
  get subtotal(): number { return this.items.reduce((sum, line) => sum + line.price, 0); }
  isSelected(kind: EcbCourseKind, courseId: number): boolean {
    return this.items.some(line => line.kind === kind && line.courseId === courseId);
  }
  getOccurrences(kind: EcbCourseKind, courseId: number): Observable<EcbOccurrenceOptions> {
    return this.http.get<EcbOccurrenceOptions>(`${ApiConfig.baseUrl}/unified-cart/occurrences/${kind}/${courseId}`);
  }
  add(options: EcbOccurrenceOptions, occurrence: EcbOccurrence): string {
    if (!options.occurrences.some(x => x.id === occurrence.id && x.seatsAvailable > 0)) {
      return 'This occurrence is no longer available. Please choose another date.';
    }
    if (occurrence.price <= 0) return 'Booking price is not configured for this date. Please contact ECB Training.';
    if (this.items.some(line => line.currency.toUpperCase() !== options.currency.toUpperCase())) {
      return 'Courses with different currencies must be purchased separately.';
    }
    const line: EcbCartLine = {
      kind: options.kind, courseId: options.courseId, occurrenceId: occurrence.id,
      title: options.title, code: options.code, imageUrl: options.imageUrl,
      currency: options.currency, price: occurrence.price,
      startDate: occurrence.startDate, endDate: occurrence.endDate,
      location: occurrence.location, time: occurrence.time, deliveryMode: occurrence.deliveryMode
    };
    // One place per course; choosing another date replaces it, never silently duplicates it.
    this.commit([...this.items.filter(x => !(x.kind === line.kind && x.courseId === line.courseId)), line]);
    return '';
  }
  remove(kind: EcbCourseKind, courseId: number): void {
    this.commit(this.items.filter(line => !(line.kind === kind && line.courseId === courseId)));
  }
  clear(): void { this.commit([]); }
  checkout(details: EcbCheckoutRequest): Observable<{ checkoutUrl: string; sessionId: string; orderId: number }> {
    return this.http.post<{ checkoutUrl: string; sessionId: string; orderId: number }>(
      `${ApiConfig.baseUrl}/unified-cart/checkout`, details);
  }
  checkoutResult(sessionId: string): Observable<{ status: string; orderNo: string; amount: number; currency: string }> {
    return this.http.get<{ status: string; orderNo: string; amount: number; currency: string }>(
      `${ApiConfig.baseUrl}/unified-cart/checkout-result`, { params: { sessionId } });
  }
  private read(): EcbCartLine[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.key) || '[]');
      return Array.isArray(parsed) ? parsed.filter(x => ['SHORT', 'QUALIFICATION'].includes(x?.kind) &&
        Number.isInteger(x?.courseId) && Number.isInteger(x?.occurrenceId)) : [];
    } catch { return []; }
  }
  private commit(lines: EcbCartLine[]): void {
    localStorage.setItem(this.key, JSON.stringify(lines));
    this.items$.next(lines);
  }
}
